import React, { useRef, useEffect } from 'react'
import { Card, Switch, Tooltip } from '@blueprintjs/core'
import EChartsReact from 'echarts-for-react'
import './SegmentDistributionChart.css'

/**
 * SegmentDistributionChart component renders a chart displaying segment distributions.
 * @param {Object} props - The component props.
 * @param {Array} props.segmentDistribution - Array of segment objects with keys such as name, value, and percentage.
 * @param {Array} props.COLORS - Array of colors used to style chart bars.
 * @param {boolean} props.isWeighted - Whether the data being displayed is weighted or not.
 * @param {boolean} props.useWeightedData - Current state of weighted data usage.
 * @param {Function} props.setUseWeightedData - Function to toggle weighted data usage.
 * @param {boolean} props.hasWeightedData - Whether weighted data is available.
 * @param {boolean} props.isLoading - Whether the segmentation data is currently loading.
 */
export function SegmentDistributionChart({
  segmentDistribution,
  COLORS,
  isWeighted = false,
  useWeightedData,
  setUseWeightedData,
  hasWeightedData,
  isLoading,
}) {
  const chartRef = useRef(null)
  const containerRef = useRef(null)

  // Use ResizeObserver to handle flex layout timing issues
  useEffect(() => {
    if (!containerRef.current || !chartRef.current) return

    const resizeObserver = new ResizeObserver(() => {
      const chartInstance = chartRef.current?.getEchartsInstance()
      if (chartInstance) {
        chartInstance.resize()
      }
    })

    resizeObserver.observe(containerRef.current)

    return () => {
      resizeObserver.disconnect()
    }
  }, [segmentDistribution])

  return (
    <Card className="segmentation-distribution-chart-card">
      <div ref={containerRef} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div className="chart-header">
          <h3 className="chart-title">Segments Distribution</h3>

          {/* Data type toggle switch */}
          {hasWeightedData && (
            <div className="switch-control-container">
              <Tooltip
                content="Weights apply to distribution only. Scores unaffected. Auto-enabled if available."
                position="top"
              >
                <Switch
                  checked={useWeightedData}
                  label={'Weights: ' + (useWeightedData ? ' On' : ' Off')}
                  onChange={e => setUseWeightedData(e.target.checked)}
                  alignIndicator="right"
                  className="data-type-switch"
                />
              </Tooltip>
            </div>
          )}
        </div>
        <EChartsReact
          ref={chartRef}
          className="segmentation-explorer-description-barchart"
          style={{ height: '100%', width: '100%' }}
          showLoading={isLoading}
          option={{
          tooltip: {
            trigger: 'axis',
            borderColor: '#ccc',
            axisPointer: {
              type: 'shadow',
            },
            formatter: params => {
              const { name, value } = params[0]
              const percentage = segmentDistribution.find(
                item => item.name === name
              ).percentage
              const dataType = isWeighted ? 'Weighted Sum' : 'Respondents'
              const formattedValue = isWeighted
                ? parseFloat(value).toFixed(2)
                : value
              return `${name}<br/>${dataType}: ${formattedValue}<br/>Percentage: ${parseFloat(
                percentage
              ).toFixed(2)}%`
            },
          },
          toolbox: {
            show: true,
            feature: {
              dataView: { show: true, readOnly: false },
              saveAsImage: { show: true },
            },
          },
          xAxis: {
            type: 'category',
            data: segmentDistribution.map(item => item.name),
            axisLabel: {
              rotate: 45,
              interval: 0,
            },
          },
          yAxis: {
            type: 'value',
            name: isWeighted ? 'Weighted Sum' : 'Respondents',
          },
          series: [
            {
              name: isWeighted ? 'Weighted Sum' : 'Respondents',
              type: 'bar',
              data: segmentDistribution.map(item => ({
                value: item.value,
                percentage: item.percentage,
              })),
              itemStyle: {
                color: params => COLORS[params.dataIndex % COLORS.length],
              },
              label: {
                show: true,
                position: 'inside',
                formatter: params =>
                  `${parseFloat(params.data.percentage).toFixed(2)}%`,
              },
            },
          ],
        }}
      />
      </div>
    </Card>
  )
}
